import api from '@pray/shared/utils/api';

/**
 * Field update operations for bulk edit
 */
export type TitleOperation = 'insert_beginning' | 'insert_end' | 'replace_all';
export type DescriptionOperation = 'insert_beginning' | 'insert_end' | 'replace_all' | 'remove';
export type TagsOperation = 'add' | 'replace_all' | 'remove';
export type VisibilityValue = 'public' | 'private' | 'scheduled';

/**
 * Field update payloads
 */
export interface TitleUpdate {
  field: 'title';
  operation: TitleOperation;
  value: string;
}

export interface DescriptionUpdate {
  field: 'description';
  operation: DescriptionOperation;
  value?: string;
}

export interface TagsUpdate {
  field: 'tags';
  operation: TagsOperation;
  tag_names: string[];
}

export interface VisibilityUpdate {
  field: 'visibility';
  operation: 'set';
  value: VisibilityValue;
}

export type FieldUpdate = TitleUpdate | DescriptionUpdate | TagsUpdate | VisibilityUpdate;

/**
 * Parameters for bulk update
 */
export interface BulkUpdateArtistContentParams {
  artistId: string;
  contentIds: string[];
  updates: FieldUpdate[];
}

/**
 * Response from bulk update
 */
export interface BulkUpdateArtistContentResponse {
  data: {
    success: boolean;
    updated_count: number;
    errors: Array<{ content_id?: string; error: string }>;
  };
}

/**
 * Bulk update multiple content items with various field changes
 */
export async function bulkUpdateArtistContent({
  artistId,
  contentIds,
  updates,
}: BulkUpdateArtistContentParams): Promise<BulkUpdateArtistContentResponse> {
  if (!artistId) throw Error('Artist ID is required');
  if (!contentIds || contentIds.length === 0) throw Error('Content IDs are required');
  if (!updates || updates.length === 0) throw Error('Updates are required');

  const url = `/web/studio/artists/${artistId}/content/bulk`;

  const response = await api.executePutRequest<BulkUpdateArtistContentResponse>(url, {
    content_ids: contentIds,
    updates,
  });

  return response.data as BulkUpdateArtistContentResponse;
}
